import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import api from '../services/api';
import { auth } from '../config/firebase';

const UserDesignationsContext = createContext();

// Session storage keys
const DESIGNATIONS_CACHE_KEY = 'vibary_designations_cache';
const VISITED_IDS_CACHE_KEY = 'vibary_visited_ids_cache';

// Get cached designations from sessionStorage
const getCachedDesignations = () => {
  try {
    const cached = sessionStorage.getItem(DESIGNATIONS_CACHE_KEY);
    if (cached) {
      return JSON.parse(cached);
    }
  } catch {
    // Ignore errors
  }
  return {};
};

// Get cached visited IDs from sessionStorage
const getCachedVisitedIds = () => {
  try {
    const cached = sessionStorage.getItem(VISITED_IDS_CACHE_KEY);
    if (cached) {
      return new Set(JSON.parse(cached));
    }
  } catch {
    // Ignore errors
  }
  return new Set();
};

// Save designations to sessionStorage cache
const setCachedDesignations = (data) => {
  try {
    if (data && Object.keys(data).length > 0) {
      sessionStorage.setItem(DESIGNATIONS_CACHE_KEY, JSON.stringify(data));
    } else {
      sessionStorage.removeItem(DESIGNATIONS_CACHE_KEY);
    }
  } catch (error) {
    // Storage quota exceeded - clear and don't cache
    if (error.name === 'QuotaExceededError') {
      try {
        sessionStorage.removeItem(DESIGNATIONS_CACHE_KEY);
      } catch {
        // Ignore cleanup errors
      }
    }
  }
};

// Save visited IDs to sessionStorage cache
const setCachedVisitedIds = (ids) => {
  try {
    if (ids && ids.size > 0) {
      sessionStorage.setItem(VISITED_IDS_CACHE_KEY, JSON.stringify([...ids]));
    } else {
      sessionStorage.removeItem(VISITED_IDS_CACHE_KEY);
    }
  } catch (error) {
    // Storage quota exceeded - clear and don't cache
    if (error.name === 'QuotaExceededError') {
      try {
        sessionStorage.removeItem(VISITED_IDS_CACHE_KEY);
      } catch {
        // Ignore cleanup errors
      }
    }
  }
};

// Clear all caches (used on logout or user change)
const clearAllCaches = () => {
  try {
    sessionStorage.removeItem(DESIGNATIONS_CACHE_KEY);
    sessionStorage.removeItem(VISITED_IDS_CACHE_KEY);
  } catch {
    // Ignore errors
  }
};

export function useUserDesignations() {
  const context = useContext(UserDesignationsContext);
  if (!context) {
    throw new Error('useUserDesignations must be used within a UserDesignationsProvider');
  }
  return context;
}

export function UserDesignationsProvider({ children }) {
  // Full designations (loaded lazily when needed)
  const [designations, setDesignations] = useState(() => getCachedDesignations());
  const [designationsLoaded, setDesignationsLoaded] = useState(false);

  // Lightweight visited IDs (loaded immediately on login)
  const [visitedPlaceIds, setVisitedPlaceIds] = useState(() => getCachedVisitedIds());
  const [visitedIdsLoaded, setVisitedIdsLoaded] = useState(getCachedVisitedIds().size > 0);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Track if we're currently fetching to prevent duplicate requests
  const fetchingDesignations = useRef(false);

  // Fetch lightweight visited place IDs (called on login)
  const fetchVisitedPlaceIds = useCallback(async () => {
    if (!auth.currentUser) {
      return;
    }
    try {
      const response = await api.getVisitedPlaceIds();
      const ids = response.data || [];
      const idsSet = new Set(ids);
      setVisitedPlaceIds(idsSet);
      setCachedVisitedIds(idsSet);
      setVisitedIdsLoaded(true);
    } catch (err) {
      console.error('Failed to fetch visited place IDs:', err);
      // Keep cached data on error
    }
  }, []);

  // Fetch all designations (called lazily when full data is needed)
  const fetchDesignations = useCallback(async () => {
    if (!auth.currentUser || fetchingDesignations.current) {
      return;
    }
    try {
      fetchingDesignations.current = true;
      setLoading(true);
      const response = await api.getUserDesignations();
      const data = response.data || {};
      setDesignations(data);
      setCachedDesignations(data);
      setDesignationsLoaded(true);
      setError(null);

      // Also update visitedPlaceIds from the full data to keep them in sync
      const visited = new Set();
      Object.entries(data).forEach(([placeId, d]) => {
        if (d?.badge && d.badge !== 'want-to-go') {
          visited.add(placeId);
        }
      });
      setVisitedPlaceIds(visited);
      setCachedVisitedIds(visited);
    } catch (err) {
      console.error('Failed to fetch designations:', err);
      setError(err.message);
      // Keep cached data on error
    } finally {
      setLoading(false);
      fetchingDesignations.current = false;
    }
  }, []);

  // Get designation for a specific place (triggers full load if needed)
  const getDesignation = useCallback((placeId) => {
    return designations[placeId] || null;
  }, [designations]);

  // Ensure full designations are loaded (for pages that need full data)
  const ensureDesignationsLoaded = useCallback(async () => {
    if (!designationsLoaded && !fetchingDesignations.current) {
      await fetchDesignations();
    }
  }, [designationsLoaded, fetchDesignations]);

  // Set/update designation for a place
  const setDesignation = useCallback(async (placeId, designationData) => {
    try {
      const response = await api.setDesignation(placeId, designationData);
      const updatedDesignation = response.data;

      // Update local designations state
      setDesignations(prev => {
        const updated = {
          ...prev,
          [placeId]: updatedDesignation,
        };
        setCachedDesignations(updated);
        return updated;
      });

      // Update visitedPlaceIds based on new badge
      const isVisited = updatedDesignation.badge && updatedDesignation.badge !== 'want-to-go';
      setVisitedPlaceIds(prev => {
        const updated = new Set(prev);
        if (isVisited) {
          updated.add(placeId);
        } else {
          updated.delete(placeId);
        }
        setCachedVisitedIds(updated);
        return updated;
      });

      return updatedDesignation;
    } catch (err) {
      console.error('Failed to set designation:', err);
      throw err;
    }
  }, []);

  // Delete designation for a place (full reset - removes from all lists)
  const deleteDesignation = useCallback(async (placeId) => {
    try {
      await api.deleteDesignation(placeId);

      // Update local designations state
      setDesignations(prev => {
        const updated = { ...prev };
        delete updated[placeId];
        setCachedDesignations(updated);
        return updated;
      });

      // Remove from visitedPlaceIds
      setVisitedPlaceIds(prev => {
        const updated = new Set(prev);
        updated.delete(placeId);
        setCachedVisitedIds(updated);
        return updated;
      });
    } catch (err) {
      console.error('Failed to delete designation:', err);
      throw err;
    }
  }, []);

  // Update privacy setting for a place
  const updatePrivacy = useCallback(async (placeId, isPrivate) => {
    try {
      await api.updateDesignationPrivacy(placeId, isPrivate);

      // Update local state
      setDesignations(prev => {
        if (!prev[placeId]) return prev;
        const updated = {
          ...prev,
          [placeId]: {
            ...prev[placeId],
            isPrivate,
          },
        };
        setCachedDesignations(updated);
        return updated;
      });

      return { isPrivate };
    } catch (err) {
      console.error('Failed to update privacy:', err);
      throw err;
    }
  }, []);

  // Batch get designations for multiple places
  const getDesignationsBatch = useCallback(async (placeIds) => {
    if (!placeIds || placeIds.length === 0) {
      return {};
    }

    // First check what we have in cache
    const cached = {};
    const missing = [];

    for (const placeId of placeIds) {
      if (designations[placeId]) {
        cached[placeId] = designations[placeId];
      } else {
        missing.push(placeId);
      }
    }

    // If we have everything cached, return immediately
    if (missing.length === 0) {
      return cached;
    }

    // Fetch missing ones from API
    try {
      const response = await api.getDesignationsBatch(missing);
      const fetched = response.data || {};

      // Merge with existing designations
      setDesignations(prev => {
        const updated = { ...prev, ...fetched };
        setCachedDesignations(updated);
        return updated;
      });

      return { ...cached, ...fetched };
    } catch (err) {
      console.error('Failed to fetch designations batch:', err);
      return cached;
    }
  }, [designations]);

  // Clear all designations (for logout)
  const clearDesignations = useCallback(() => {
    setDesignations({});
    setVisitedPlaceIds(new Set());
    setDesignationsLoaded(false);
    setVisitedIdsLoaded(false);
    clearAllCaches();
  }, []);

  // Helper to check if a place is visited (for convenience)
  const isPlaceVisited = useCallback((placeId) => {
    return visitedPlaceIds.has(placeId);
  }, [visitedPlaceIds]);

  // Listen for auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        // Fetch lightweight visited IDs immediately (fast, for % visited calculation)
        fetchVisitedPlaceIds();
        // Don't fetch full designations until actually needed
      } else {
        clearDesignations();
      }
    });

    return () => unsubscribe();
  }, [fetchVisitedPlaceIds, clearDesignations]);

  const value = useMemo(() => ({
    designations,
    loading,
    error,
    getDesignation,
    setDesignation,
    deleteDesignation,
    updatePrivacy,
    getDesignationsBatch,
    fetchDesignations,
    ensureDesignationsLoaded,
    clearDesignations,
    // Performance optimization: pre-computed visited places set
    visitedPlaceIds,
    visitedIdsLoaded,
    isPlaceVisited,
  }), [
    designations,
    loading,
    error,
    getDesignation,
    setDesignation,
    deleteDesignation,
    updatePrivacy,
    getDesignationsBatch,
    fetchDesignations,
    ensureDesignationsLoaded,
    clearDesignations,
    visitedPlaceIds,
    visitedIdsLoaded,
    isPlaceVisited,
  ]);

  return (
    <UserDesignationsContext.Provider value={value}>
      {children}
    </UserDesignationsContext.Provider>
  );
}

export default UserDesignationsContext;
